import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../shadcn/components/ui/tabs";
import { Input } from "../../../shadcn/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shadcn/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../shadcn/components/ui/popover";
import { Calendar } from "../../../shadcn/components/ui/calendar";
import { Button } from "../../../shadcn/components/ui/button";
import { getNotificationsForBanker } from "services";
import NotificationsDetailsWrapper from "./tableComponents/NotificationsDetailWrapper";
import CalendarIcon from "../../../icons/CalendarIcon";
import { DateTime } from "luxon";

const BankerNotificationPage = () => {
  const currentBranch = useSelector((state: any) => {
    return state.bankerAuth.currentBranch;
  });
  const [biddersData, setBiddersData] = useState<any>([]);
  const [refreshPage, setRefreshPage] = useState(true);
  const [sortBy, setSortBy] = useState("submitted_date_latest");
  const [borrowerSearch, setBorrowerSearch] = useState("");
  const [auctionDate, setAuctionDate] = useState<Date | undefined>(undefined);
  const userProfile = useSelector((state: any) => {
    return state.bankerAuth.user;
  });

  const fetchKYCData = async () => {
    try {
      const res: any = await getNotificationsForBanker({
        branch_uuid: currentBranch?.value,
        status: ["pending", "accepted", "rejected"],
        approver_uuid: userProfile?.uuid,
      });

      if (res && res?.[0]) {
        console.log("res", res);

        setBiddersData(res[0]);
        setRefreshPage(false);
      } else setBiddersData([]);
    } catch (err) {
      setRefreshPage(false);
      console.log("errors here", err);
    }
  };
  useEffect(() => {
    fetchKYCData();
  }, [currentBranch]);

  useEffect(() => {
    if (refreshPage) fetchKYCData();
  }, [refreshPage]);

  const notificationsData = [
    {
      title:
        biddersData?.pending?.length > 0
          ? `Pending (${biddersData?.pending.length})`
          : "Pending",
      value: "pending",
      component: (
        <NotificationsDetailsWrapper
          eventData={biddersData?.pending}
          status={["pending"]}
          sortBy={sortBy}
          borrowerSearch={borrowerSearch}
          auctionDate={auctionDate}
        />
      ),
    },
    {
      title:
        biddersData?.accepted?.length > 0
          ? `Completed (${biddersData?.accepted?.length})`
          : "Completed",
      value: "accepted",
      component: (
        <NotificationsDetailsWrapper
          eventData={biddersData?.accepted}
          status={["accepted"]}
          sortBy={sortBy}
          borrowerSearch={borrowerSearch}
          auctionDate={auctionDate}
        />
      ),
    },
    {
      title:
        biddersData?.rejected?.length > 0
          ? `Rejected (${biddersData?.rejected?.length})`
          : "Rejected",
      value: "rejected",
      component: (
        <NotificationsDetailsWrapper
          eventData={biddersData?.rejected}
          status={["rejected"]}
          sortBy={sortBy}
          borrowerSearch={borrowerSearch}
          auctionDate={auctionDate}
        />
      ),
    },
  ];
  return (
    <div className="w-full px-4 lg:px-8 mt-5 pb-4">
      {/* Header Section */}
      <div className="mb-2">
        <h2 className="text-xl font-bold text-gray-800 mb-2">Bid Requests</h2>
        <p className="text-sm text-gray-600">
          Manage KYC & EMD verification before allowing bidders into the auction
          room.
        </p>
      </div>

      <Tabs defaultValue="pending" className="w-full">
        {/* Toolbar: Tabs on left, Filters on right */}
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-3 mb-2 flex-wrap">
          <div className="overflow-auto scrollbar-hide">
            <TabsList className="inline-flex h-auto items-center rounded-full bg-[#f4f4f4] p-1 text-muted-foreground">
              {notificationsData?.map((item, index) => (
                <TabsTrigger
                  value={item?.value}
                  className="capitalize border-none bg-transparent px-4 py-2 text-xs text-[#555] rounded-full data-[state=active]:bg-white data-[state=active]:text-[#111] data-[state=active]:shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
                  key={`residential_tab_${item.value}_${index}`}
                >
                  {item?.title}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <div className="flex flex-col text-xs text-[#555]">
              <label htmlFor="sortSelect" className="mb-0.5">
                Sort by
              </label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger
                  id="sortSelect"
                  className="h-8 px-2 py-1 text-xs border border-[#ddd] rounded-md min-w-[140px]"
                >
                  <SelectValue placeholder="Select sort option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="submitted_date_latest">
                    Submission Date (Latest first)
                  </SelectItem>
                  <SelectItem value="submitted_date_oldest">
                    Submission Date (Oldest first)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col text-xs text-[#555]">
              <label htmlFor="borrowerFilter" className="mb-0.5">
                Borrower name
              </label>
              <Input
                id="borrowerFilter"
                type="text"
                placeholder="Search borrower..."
                value={borrowerSearch}
                onChange={(e) => setBorrowerSearch(e.target.value)}
                className="h-8 px-2 py-1 text-xs border border-[#ddd] rounded-md min-w-[140px]"
              />
            </div>

            <div className="flex flex-col text-xs text-[#555]">
              <label htmlFor="auctionDateFilter" className="mb-0.5">
                Auction date
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="auctionDateFilter"
                    variant="outline"
                    className="h-8 px-2 py-1 text-xs border border-[#ddd] rounded-md min-w-[140px] justify-start font-normal bg-white hover:bg-white"
                  >
                    {auctionDate ? (
                      DateTime.fromJSDate(auctionDate).toFormat("dd/MM/yyyy")
                    ) : (
                      <span className="text-gray-400">dd/mm/yyyy</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 text-gray-500" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={auctionDate}
                    onSelect={setAuctionDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {notificationsData?.map((item, index) => (
          <TabsContent
            className="mt-2.5"
            value={item.value}
            key={`residential_tab_trigger_${item.value}_${index}`}
          >
            {item?.component}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default BankerNotificationPage;
